import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { CircleAlert, CircleCheck, Info, TriangleAlert, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import './notice.css'

export type NoticeTone = 'info' | 'success' | 'warning' | 'error'

type NoticeContent = Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'role' | 'children'> & {
  /** A short heading, such as “Couldn’t save the event”. Optional when the message says it all. */
  title?: ReactNode
  /** The message: what happened and what to do next. Line breaks in text are kept. */
  children: ReactNode
  /** The next step, such as a Button or ButtonLink. */
  action?: ReactNode
}

type NoticeAnnouncement =
  | {
      tone: 'error'
      /** Announces the error immediately (`role="alert"`). Only for an error that appears in response to
       *  something the person just did, such as a failed sign in. Otherwise the notice is a polite status. */
      alert?: boolean
    }
  | { tone?: Exclude<NoticeTone, 'error'>; alert?: never }

export type NoticeProps = NoticeContent & NoticeAnnouncement

const tones: Record<NoticeTone, { Icon: LucideIcon; label?: string }> = {
  info: { Icon: Info },
  success: { Icon: CircleCheck, label: 'Success' },
  warning: { Icon: TriangleAlert, label: 'Warning' },
  error: { Icon: CircleAlert, label: 'Error' },
}

/** An inline message about the current view: its impact and the next step. The icon and title carry the tone with color. */
export const Notice = forwardRef<HTMLDivElement, NoticeProps>(function Notice(
  { tone = 'info', alert = false, title, action, className, children, ...props },
  ref,
) {
  const { Icon, label } = tones[tone]
  return (
    <div ref={ref} role={alert ? 'alert' : 'status'} className={cn('play-notice', `play-notice--${tone}`, className)} {...props}>
      <Icon className="play-notice__icon" aria-hidden="true" />
      <div className="play-notice__body">
        {label && <span className="play-notice__tone">{label}: </span>}
        {title && <p className="play-notice__title">{title}</p>}
        <div className="play-notice__message">{children}</div>
        {action && <div className="play-notice__action">{action}</div>}
      </div>
    </div>
  )
})
